import React, { useRef, useState } from 'react';
import { Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { withBrandName } from '../../../config/appConfig';
import { useCreateUploadHandler } from '../dataHandlers/createUploadHandler';
import { downloadTemplate } from '../utils/formatters';

// Design-reference dropzone: one full-width dashed card. Dropping/choosing an
// .xlsx uploads immediately; while in flight the card swaps to a filename +
// status badge + progress strip. Ingestion progress then lives in the batch
// log below. Reconciliation is refreshed by the scheduled dbt job.
const UploadDropzone = ({ onUploaded }) => {
  const { reconciliation: r } = useTranslatedMessages();
  const t = r.dropzone ?? {};
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [done, setDone] = useState(false);

  const { onUpload, isPending } = useCreateUploadHandler({
    onSuccess: (session) => {
      setDone(true);
      const successMessage = withBrandName(
        t.success ?? 'Batch received — ingesting for the next scheduled reconciliation',
      );
      dispatch(showSnackbar({ message: successMessage, severity: 'success' }));
      onUploaded?.(session);
      setTimeout(() => {
        setFileName(null);
        setDone(false);
      }, 1800);
    },
  });

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      dispatch(showSnackbar({ message: t.only_xlsx ?? 'Only .xlsx files are supported.', severity: 'error' }));
      return;
    }
    setFileName(file.name);
    setDone(false);
    onUpload(file);
  };

  const busy = isPending || done;

  return (
    <Paper
      variant="outlined"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
      onClick={() => !busy && inputRef.current?.click()}
      sx={{
        p: 4,
        cursor: busy ? 'default' : 'pointer',
        borderStyle: 'dashed',
        borderWidth: 2,
        borderColor: dragOver ? 'primary.main' : 'divider',
        bgcolor: dragOver ? 'rgba(82, 82, 255, 0.05)' : 'rgba(0,0,0,0.015)',
        transition: 'all 120ms',
        '&:hover': busy ? {} : { bgcolor: 'rgba(0,0,0,0.04)' },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {!busy ? (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              height: 48,
              width: 48,
              borderRadius: '50%',
              bgcolor: 'rgba(82, 82, 255, 0.10)',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CloudUploadOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 500 }}>{t.title ?? 'Drop ERP export here'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t.hint ?? '.xlsx · Replaces or updates matches for the selected tracking period.'}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              downloadTemplate().catch(() => {
                dispatch(
                  showSnackbar({
                    message: t.template_unavailable ?? 'There is an issue with the template. It will be fixed.',
                    severity: 'error',
                  }),
                );
              });
            }}
          >
            {t.template ?? 'Download template'}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {done ? (
              <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: '#059669' }} />
            ) : (
              <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} color="primary" />
            )}
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {fileName}
            </Typography>
            <Chip
              size="small"
              label={done ? (t.validating ?? 'Validating schema') : (t.uploading ?? 'Uploading')}
              sx={{ height: 18, fontSize: 10 }}
            />
          </Stack>
          <LinearProgress
            variant={done ? 'determinate' : 'indeterminate'}
            value={100}
            sx={{ height: 4, borderRadius: 2 }}
          />
          {done && (
            <Typography variant="caption" color="text.secondary">
              {t.validating_detail ?? 'Checking columns, TRN validity and duplicate keys…'}
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default UploadDropzone;
